import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';

import {
  UpdateDocumentUnderstandingAgentSettingsPayload,
  useDocumentUnderstandingAgentSettings,
  useUpdateDocumentUnderstandingAgentSettings,
} from 'services/repositories/documentUnderstandingAgent/documentUnderstandingAgent';
import { NotificationType, useNotificationStore } from 'stores/notifications/notifications';
import { processSubmissionErrors } from 'support/helpers/errors/errors';
import { EmptyState } from 'components/EmptyState/EmptyState';
import { Button } from 'components/Form/Button/Button';
import { Form } from 'components/Form/Form';
import { ValidationErrors } from 'components/Form/ValidationErrors/ValidationErrors';
import { LoadingPage } from 'components/Loading/LoadingPage';

import {
  DEFAULT_DOCUMENT_UNDERSTANDING_AGENT_FORM_VALUES,
  EditableDocumentUnderstandingAgentSections,
} from '../../helpers';
import { CustomInstructionsSection } from './components/CustomInstructionsSection';
import { MailboxSection } from './components/MailboxSection';
import { PriceStrategySection } from './components/PriceStrategySection';
import { ReplyNotificationsSection } from './components/ReplyNotificationsSection';
import { SenderFilterSection } from './components/SenderFilterSection';
import { UnitsOfMeasureSection } from './components/UnitsOfMeasureSection';

const FORM_ID = 'document-understanding-agent-global-form';

export const GlobalSettingsTab = () => {
  const { t } = useTranslation();
  const { addNotification } = useNotificationStore();
  const { data: settings, isLoading, isError, refetch } = useDocumentUnderstandingAgentSettings();
  const { mutate, isLoading: isSaving } = useUpdateDocumentUnderstandingAgentSettings();
  const [nonInputErrors, setNonInputErrors] = useState<Array<string>>([]);

  const {
    control,
    handleSubmit,
    reset,
    setError,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<EditableDocumentUnderstandingAgentSections>();

  const whitelist = watch('sender_filter.whitelist');
  const blacklist = watch('sender_filter.blacklist');
  const replyEmails = watch('replies_filter.emails');

  if (isLoading) {
    return <LoadingPage />;
  }

  if (isError || !settings) {
    return (
      <EmptyState
        icon={<ExclamationCircleIcon />}
        title={t('settings:documentUnderstandingAgent.global.error.title')}
        description={t('settings:documentUnderstandingAgent.global.error.description') ?? undefined}
        ctaText={t('settings:documentUnderstandingAgent.global.error.retry') ?? undefined}
        ctaOnClick={() => refetch()}
      />
    );
  }

  const onSubmit = (submission: EditableDocumentUnderstandingAgentSections) => {
    setNonInputErrors([]);

    const payload: UpdateDocumentUnderstandingAgentSettingsPayload = {
      extraction: { prices_strategy: submission.extraction.prices_strategy },
      sender_filter: { whitelist: submission.sender_filter.whitelist, blacklist: submission.sender_filter.blacklist },
      replies_filter: {
        emails: submission.replies_filter.emails,
        suppress_on_success: !submission.replies_filter.notifications_enabled,
      },
    };

    mutate(payload, {
      onSuccess: () => {
        setNonInputErrors([]);
        addNotification(t('settings:documentUnderstandingAgent.global.saved'), NotificationType.success);
      },
      onError: (error) => {
        processSubmissionErrors<EditableDocumentUnderstandingAgentSections>({
          error,
          setInputError: setError,
          setNonInputErrors,
          defaultData: DEFAULT_DOCUMENT_UNDERSTANDING_AGENT_FORM_VALUES,
          t,
        });
      },
    });
  };

  return (
    <Form
      onSubmit={handleSubmit(onSubmit)}
      id={FORM_ID}
      data-testid={FORM_ID}
      className="w-full divide-y divide-gray-200 *:py-6 *:first:pt-0 *:last:pb-0"
    >
      <ValidationErrors errors={nonInputErrors} />

      <MailboxSection email={settings.email} />

      <SenderFilterSection
        whitelist={whitelist}
        blacklist={blacklist}
        disabled={isSaving}
        onApply={(nextWhitelist, nextBlacklist) => {
          setValue('sender_filter.whitelist', nextWhitelist, { shouldDirty: true });
          setValue('sender_filter.blacklist', nextBlacklist, { shouldDirty: true });
        }}
      />

      <ReplyNotificationsSection
        control={control}
        replyEmails={replyEmails}
        disabled={isSaving}
        onApplyRecipients={(nextEmails) => setValue('replies_filter.emails', nextEmails, { shouldDirty: true })}
      />

      <PriceStrategySection control={control} error={errors.extraction?.prices_strategy} disabled={isSaving} />

      <UnitsOfMeasureSection units={settings.extraction.preferred_units_of_measure} />

      <CustomInstructionsSection customInstructions={settings.extraction.custom_instructions} />

      <div className="flex items-center gap-4">
        <p className="text-sm text-gray-500">{t('settings:documentUnderstandingAgent.global.saveHint')}</p>
        <div className="flex shrink-0 items-center">
          <Button
            className="mr-3"
            variant="secondary"
            onClick={() => reset()}
            disabled={isSaving}
            analyticsId="document-understanding-agent:global-settings-cancel"
          >
            {t('common:cancel')}
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            loading={isSaving}
            analyticsId="document-understanding-agent:global-settings-save"
          >
            {t('common:save')}
          </Button>
        </div>
      </div>
    </Form>
  );
};
